import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * StockMovement : famille "mouvement de stock".
 *
 * Entrée, sortie, transfert ou ajustement daté qui modifie une quantité en
 * stock, avec sa cause (réception, consommation, perte, inventaire).
 * Construite sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas l'opération métier concrète (réassort grossiste,
 * pièce consommée, casse à l'inventaire) : il connaît la famille générique
 * `stockMovement`. Le SENS du mouvement (entrée, sortie, transfert, ajustement)
 * est porté par le discriminant `type` ; la CAUSE plus fine (réception,
 * consommation, perte, inventaire) par le champ métier `reason`.
 *
 * Cœur de la famille : un mouvement porte toujours sur un article (`productId`)
 * et déplace une quantité (`quantity`), les deux sont donc requis. Le sens du
 * signe de `quantity` (ajout ou retrait) est laissé à l'app via `type` et les
 * emplacements source / destination.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `fromPlaceId` / `toPlaceId` (emplacements `place`), `lotId` (sous-ensemble
 * tracé), `orderId` / `workOrderId` (la pièce source du mouvement) et
 * `shipmentId` (expédition physique correspondante), tous optionnels. Le
 * `stock` (niveau disponible) n'est PAS une référence stockée ici : il est
 * dérivé de `productId` + emplacement et recalculé à partir des mouvements.
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible n'est
 * signalé sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille stockMovement (hors champs standard du socle). */
export const stockMovementCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "entree", "sortie", "transfert", "ajustement"). */
  type: z.string().min(1),
  /** Article entré ou sorti (référence `product`, cœur de la famille). Requis. */
  productId: z.string().min(1),
  /** Quantité déplacée par le mouvement (entière), cœur de la famille. Requis. */
  quantity: z.number().int(),
  /** Emplacement source du mouvement (référence `place`). Optionnel. */
  fromPlaceId: z.string().min(1).optional(),
  /** Emplacement destination du mouvement (référence `place`). Optionnel. */
  toPlaceId: z.string().min(1).optional(),
  /** Cause métier libre (ex. "reception", "consommation", "perte", "inventaire"). Optionnel. */
  reason: z.string().min(1).optional(),
  /** Sous-ensemble tracé déplacé (référence `lot`). Optionnel. */
  lotId: z.string().min(1).optional(),
  /** Date effective du mouvement. Optionnel. */
  movedAt: z.date().optional(),
  /** Pièce source : commande à l'origine de la réception (référence `order`). Optionnel. */
  orderId: z.string().min(1).optional(),
  /** Pièce source : intervention qui consomme les pièces (référence `work-order`). Optionnel. */
  workOrderId: z.string().min(1).optional(),
  /** Expédition physique correspondante (référence `shipment`). Optionnel. */
  shipmentId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille stockMovement (champs standard + champs métier). */
export const StockMovementSchema = z.object({
  ...baseEntityFields,
  ...stockMovementCoreFields,
});

/** Type d'un stockMovement. */
export type StockMovement = z.infer<typeof StockMovementSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const StockMovementCreateInputSchema = z.object({
  ...stockMovementCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un stockMovement. */
export type StockMovementCreateInput = z.infer<typeof StockMovementCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const StockMovementUpdateInputSchema = z
  .object({ ...stockMovementCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un stockMovement. */
export type StockMovementUpdateInput = z.infer<typeof StockMovementUpdateInputSchema>;
